import { IntlShape } from 'react-intl';

// Delivery cycles come out of Shopify selling plans in DAYS (14/28/35/42/56/…),
// and that's the unit the whole data layer speaks: SKUs encode it
// (`0161-CGGS-V-28`), `billingPolicy.intervalCount` is sent in days, and
// `variant.selectedOptions.cycle` is a day count. Only the wording shown to the
// customer changes here — nothing upstream is converted.
//
// Every cycle currently sold is a whole number of weeks, but the picker renders
// whatever the variants expose, so a cycle that isn't divisible by 7 keeps the
// day wording rather than showing "8,5 Wochen".
//
// A 7-day cycle also keeps days: "alle 1 Woche" isn't idiomatic German, and the
// alternatives ("jede Woche" / "wöchentlich") don't fit the "Lieferung alle …"
// sentence the callers build. The smallest cycle in the catalog is 14 days, so
// this is a guard rather than a case we expect to hit. The same reasoning holds
// in English and French, so the week branch stays gated on `weeks >= 2` in every
// language rather than being pushed into the message as a plural case.
//
// Takes `intl` rather than calling a hook because several callers are not
// components — `pet-profile-update/types.ts` and `cycleCopy.tsx` build strings
// outside a render. Same shape as `getSubscriptionResume(intl)`.
export const formatCycleInterval = (intl: IntlShape, cycle: number): string => {
  const days = (count: number) => intl.formatMessage({ id: 'CYCLE.DAYS' }, { count });

  if (!Number.isFinite(cycle) || cycle <= 0) return days(cycle);
  if (cycle % 7 !== 0) return days(cycle);

  const weeks = cycle / 7;
  if (weeks < 2) return days(cycle);

  return intl.formatMessage({ id: 'CYCLE.WEEKS' }, { count: weeks });
};
